import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Wrench } from 'lucide-react';
import { useT } from '../i18n';

interface CanvasToolsMenuProps {
  /** Portal cíl v `.toolbar__actions` (`ProjectPage.tsx`) — `null`, dokud ref nestihne nastavit. */
  portalTarget: HTMLElement | null;
  children: ReactNode;
}

/**
 * Epic 15, E15-US4 — sjednocené menu pro ovládání canvasu (Zobrazení,
 * Auto-layout, Synchronizace, Change/Create script, Export), dřív rozeseté
 * jako samostatná tlačítka v pravém horním rohu canvasu (`.canvas-view-controls`,
 * zrušeno).
 *
 * `children` (skutečné panely — `ViewSettingsPanel`, `SyncPanel`,
 * `ChangeScriptPanel`, `CreateScriptPanel`, `ExportPanel`, tlačítko
 * Auto-layout) zůstávají VŽDY namountované — menu se nezavírá odmountováním
 * (na rozdíl od `ContextMenu.tsx`), jen přepnutím viditelnosti přes inline
 * styl. Kdyby se zavíralo odmountováním, zavření menu uprostřed rozjeté akce
 * (např. `SyncPanel` v půlce scanu) by tu akci zničilo i s jejím stavem.
 * Vnořené `Modal`/`ContextMenu` uvnitř jednotlivých panelů portálují samy do
 * `document.body` (`Modal.tsx` — viz jeho komentář), takže na viditelnost
 * tohohle wrapperu nejsou vázané a zůstanou funkční, i když se menu mezitím
 * schová.
 *
 * Trigger tlačítko se portáluje do `portalTarget` (slot v `.toolbar__actions`,
 * `ProjectPage.tsx`) — canvas providery (schema/canvas store) leží MIMO
 * strom, který toolbar renderuje (Epic 4 — `SchemaStoreProvider` nad
 * `Workspace`, ne v něm), takže zvednutí providerů nad celou stránku by bylo
 * invazivní; portál je jednodušší
 * (`doc/epic-15-rozsireni-projektu.md` kap. 8).
 */
export function CanvasToolsMenu({ portalTarget, children }: CanvasToolsMenuProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (bodyRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const toggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ x: rect.left, y: rect.bottom + 4 });
    }
    setOpen((prev) => !prev);
  };

  return (
    <>
      {portalTarget &&
        createPortal(
          <button type="button" ref={triggerRef} onClick={toggle}>
            <Wrench size={16} />
            {t('canvas.tools.button')}
          </button>,
          portalTarget,
        )}
      {createPortal(
        <div
          ref={bodyRef}
          className="canvas-tools-menu"
          style={{
            left: position?.x ?? 0,
            top: position?.y ?? 0,
            display: open && position ? 'flex' : 'none',
          }}
          // Kliknutí na KTEROUKOLIV položku uvnitř menu ho rovnou schová — capture
          // fáze proběhne PŘED vlastním onClick tlačítka, ale React state update
          // se promítne do DOM až po dokončení celého handleru, takže vlastní akce
          // položky (otevření Modalu/popoveru) proběhne beze změny.
          onClickCapture={() => setOpen(false)}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}
